/**
 * Admin Column Layouts API client — talks to /api/admin-column-layouts.php.
 * All requests are credentialed (admin session cookie). Backs the super-admin
 * Column Layout Designer.
 */

import { ColumnLayoutItem, ColumnLayoutScope } from '../types';

const API_BASE = window.location.hostname === 'localhost' ? 'http://chriscme/api' : '/api';

/** A stored layout row (universal when retailerId is null). */
export interface AdminColumnLayout {
  id: number;
  retailerId: number | null;
  scope: ColumnLayoutScope;
  items: ColumnLayoutItem[];
  isActive: boolean;
}

export interface ColumnLayoutRetailerSummary {
  id: number;
  name: string;
  isPartner: boolean;
}

export interface ColumnLayoutsListResponse {
  layouts: AdminColumnLayout[];
  retailers: ColumnLayoutRetailerSummary[];
}

export interface SaveLayoutPayload {
  /** null = universal default for all retailers. */
  retailerId: number | null;
  scope: ColumnLayoutScope;
  items: ColumnLayoutItem[];
  isActive?: boolean;
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export async function fetchColumnLayouts(): Promise<ColumnLayoutsListResponse> {
  const res = await fetch(`${API_BASE}/admin-column-layouts.php`, { credentials: 'include' });
  return handle<ColumnLayoutsListResponse>(res);
}

export async function saveColumnLayout(payload: SaveLayoutPayload): Promise<{ layout: AdminColumnLayout }> {
  const res = await fetch(`${API_BASE}/admin-column-layouts.php`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handle(res);
}

export async function deleteColumnLayout(retailerId: number | null, scope: ColumnLayoutScope): Promise<void> {
  const params = new URLSearchParams({
    retailer_id: retailerId === null ? 'universal' : String(retailerId),
    scope,
  });
  const res = await fetch(`${API_BASE}/admin-column-layouts.php?${params}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Delete failed (${res.status})`);
  }
}
